import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { JourneyTimeline } from './JourneyTimeline';
import { MapPin, Bus, Tablet, Gamepad2, Trophy, Navigation, Car } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0
  }
};
function UberPhoneMockup({
  variant
}: {
  variant: 'waiting' | 'ad' | 'engaged';
}) {
  return <div className="relative bg-[#1a1a1a] rounded-[24px] p-[5px] shadow-2xl w-[160px] shrink-0" data-id="element-82">
      <div className="absolute top-[5px] left-1/2 -translate-x-1/2 w-16 h-4 bg-[#1a1a1a] rounded-b-lg z-20" data-id="element-83" />
      <div className="bg-white rounded-[20px] overflow-hidden" data-id="element-84">
        {variant === 'waiting' && <>
            {/* Uber-style header */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-85">
              <div className="flex items-center gap-1" data-id="element-86">
                <span className="text-white text-[10px] font-bold" data-id="element-87">Uber</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-88">
                <Navigation className="w-2.5 h-2.5 text-green-400" data-id="element-89" />
                <span className="text-green-400 text-[7px] font-medium" data-id="element-90">
                  Live
                </span>
              </div>
            </div>
            {/* Map area */}
            <div className="relative bg-[#E8E4DA] h-[90px] overflow-hidden" data-id="element-91">
              {/* Stylized map grid */}
              <div className="absolute inset-0 opacity-30" data-id="element-92">
                <div className="absolute top-4 left-3 right-6 h-[1px] bg-gray-400" data-id="element-93" />
                <div className="absolute top-8 left-8 right-3 h-[1px] bg-gray-400" data-id="element-94" />
                <div className="absolute top-12 left-2 right-10 h-[1px] bg-gray-400" data-id="element-95" />
                <div className="absolute top-3 left-6 w-[1px] h-16 bg-gray-400" data-id="element-96" />
                <div className="absolute top-2 left-12 w-[1px] h-20 bg-gray-400" data-id="element-97" />
                <div className="absolute top-6 right-8 w-[1px] h-14 bg-gray-400" data-id="element-98" />
              </div>
              {/* Congress venue pin */}
              <div className="absolute top-6 right-5 flex flex-col items-center" data-id="element-99">
                <div className="bg-[#E60012] rounded-full w-5 h-5 flex items-center justify-center shadow-md" data-id="element-100">
                  <MapPin className="w-3 h-3 text-white" data-id="element-101" />
                </div>
                <div className="bg-white rounded px-1 py-0.5 mt-0.5 shadow-sm" data-id="element-102">
                  <span className="text-[5px] font-bold text-[#2D2926]" data-id="element-103">
                    Congress
                  </span>
                </div>
              </div>
              {/* Geo-fence radius */}
              <div className="absolute top-2 right-1 w-14 h-14 rounded-full border-2 border-[#E60012]/25 bg-[#E60012]/5" data-id="element-104" />
              {/* Car icon */}
              <div className="absolute bottom-3 left-4" data-id="element-105">
                <div className="bg-black rounded-full w-4 h-4 flex items-center justify-center" data-id="element-106">
                  <Car className="w-2.5 h-2.5 text-white" data-id="element-107" />
                </div>
              </div>
              {/* Route line */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 90" data-id="element-108">
                <path d="M 25 72 Q 60 50 80 40 Q 110 25 125 30" stroke="#276EF1" strokeWidth="2" fill="none" strokeDasharray="4 3" data-id="element-109" />
              </svg>
            </div>
            {/* Ride info */}
            <div className="bg-white px-3 py-2 border-t border-gray-100" data-id="element-110">
              <div className="flex items-center justify-between mb-1" data-id="element-111">
                <span className="text-[9px] font-bold text-black" data-id="element-112">
                  UberX arriving
                </span>
                <span className="text-[9px] font-bold text-black" data-id="element-113">3 min</span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-114">
                <div className="h-full w-2/3 bg-black rounded-full" data-id="element-115" />
              </div>
              <p className="text-[7px] text-gray-500 mt-1" data-id="element-116">
                Heading to Congress Center
              </p>
            </div>
          </>}

        {variant === 'ad' && <>
            {/* Uber header with ad overlay */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-117">
              <span className="text-white text-[10px] font-bold" data-id="element-118">Uber</span>
              <span className="text-white/40 text-[6px]" data-id="element-119">Sponsored</span>
            </div>
            {/* Geo-targeted CSL ad — branded white style */}
            <div className="bg-white p-3 relative overflow-hidden" data-id="element-120">
              {/* ERA + CSL header bar */}
              <div className="flex items-center justify-center gap-2 mb-2 pb-1.5 border-b border-gray-100" data-id="element-121">
                <span className="text-[8px] font-bold text-[#E60012]" data-id="element-122">CSL</span>
                <span className="text-gray-300 text-[6px]" data-id="element-123">|</span>
                <span className="text-[7px] font-medium text-gray-500" data-id="element-124">
                  Sponsored
                </span>
              </div>
              <div className="flex items-center gap-1 mb-1.5" data-id="element-125">
                <MapPin className="w-2 h-2 text-[#E60012]" data-id="element-126" />
                <span className="text-[#E60012] text-[5px] font-medium" data-id="element-127">
                  Near Congress Center
                </span>
              </div>
              <p className="text-[#2D2926] font-bold text-[9px] leading-tight mb-2" data-id="element-128">
                When do you escalate beyond RAAS blockade?
              </p>
              <div className="space-y-1" data-id="element-129">
                <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5" data-id="element-130">
                  <span className="text-[#2D2926] text-[7px]" data-id="element-131">
                    Persistent proteinuria &gt;1g/day
                  </span>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5" data-id="element-132">
                  <span className="text-[#2D2926] text-[7px]" data-id="element-133">
                    Declining eGFR
                  </span>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5" data-id="element-134">
                  <span className="text-[#2D2926] text-[7px]" data-id="element-135">
                    High-risk at diagnosis
                  </span>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg px-2 py-1.5" data-id="element-136">
                  <span className="text-[#2D2926] text-[7px]" data-id="element-137">
                    Case-by-case
                  </span>
                </div>
              </div>
              <div className="mt-2 bg-[#E60012] rounded-full py-1 text-center" data-id="element-138">
                <span className="text-white text-[7px] font-semibold" data-id="element-139">
                  Next question →
                </span>
              </div>
            </div>
            {/* Bottom ride status */}
            <div className="bg-white px-3 py-1.5 border-t border-gray-100" data-id="element-140">
              <div className="flex items-center justify-between" data-id="element-141">
                <span className="text-[8px] font-medium text-black" data-id="element-142">
                  Your ride is arriving
                </span>
                <span className="text-[8px] font-bold text-black" data-id="element-143">1 min</span>
              </div>
            </div>
          </>}

        {variant === 'engaged' && <>
            {/* Uber header */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-144">
              <span className="text-white text-[10px] font-bold" data-id="element-145">Uber</span>
              <span className="text-white/40 text-[6px]" data-id="element-146">Sponsored</span>
            </div>
            {/* Result + CTA — CSL branded white style */}
            <div className="bg-white p-3 relative overflow-hidden" data-id="element-147">
              {/* ERA + CSL header bar */}
              <div className="flex items-center justify-center gap-2 mb-2 pb-1.5 border-b border-gray-100" data-id="element-148">
                <span className="text-[8px] font-bold text-[#E60012]" data-id="element-149">CSL</span>
                <span className="text-gray-300 text-[6px]" data-id="element-150">|</span>
                <span className="text-[7px] font-medium text-gray-500" data-id="element-151">
                  Sponsored
                </span>
              </div>
              <div className="text-center mb-2" data-id="element-152">
                <div className="inline-flex items-center gap-1 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 mb-1.5" data-id="element-153">
                  <span className="text-green-600 text-[8px] font-bold" data-id="element-154">
                    ✓ 45% of peers agree
                  </span>
                </div>
                <p className="text-[#2D2926] text-[7px] leading-snug font-medium" data-id="element-155">
                  Persistent proteinuria &gt;1g/day — you align with most peers
                </p>
              </div>
              <div className="bg-[#FDF8F8] rounded-lg p-2 mb-2 border border-[#E60012]/10" data-id="element-156">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-157">
                  <Trophy className="w-3 h-3 text-[#E60012]" data-id="element-158" />
                  <span className="text-[#2D2926] text-[7px] font-bold" data-id="element-159">
                    Explore the full data at our booth
                  </span>
                </div>
                <p className="text-gray-500 text-[6px] leading-snug" data-id="element-160">
                  CSL Booth #215 at the Congress Center
                </p>
              </div>
              <div className="flex justify-center" data-id="element-161">
                <div className="bg-[#E60012] rounded-full px-3 py-1" data-id="element-162">
                  <span className="text-white text-[7px] font-semibold flex items-center gap-1" data-id="element-163">
                    <Navigation className="w-2 h-2" data-id="element-164" /> Get Directions
                  </span>
                </div>
              </div>
            </div>
            {/* Bottom */}
            <div className="bg-white px-3 py-1.5 border-t border-gray-100" data-id="element-165">
              <div className="flex items-center justify-between" data-id="element-166">
                <span className="text-[8px] font-medium text-black" data-id="element-167">
                  You've arrived
                </span>
                <MapPin className="w-3 h-3 text-green-500" data-id="element-168" />
              </div>
            </div>
          </>}
      </div>
    </div>;
}
export function SlideArrival() {
  return <div className="h-full w-full bg-[#FDF8F8] text-[#2D2926] p-6 md:p-10 flex flex-col" data-id="element-169">
      <JourneyTimeline currentStage={2} data-id="element-170" />

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-3 border-l-4 border-[#C5000F] pl-5" data-id="element-171">
        <div className="flex items-center gap-3 mb-1" data-id="element-172">
          <span className="text-2xl" data-id="element-173">🧲</span>
          <h2 className="text-2xl font-bold text-[#2D2926]" data-id="element-174">
            Stage 2: Vicinity — Brand Nudging
          </h2>
        </div>
        <p className="text-base text-[#C5000F] font-medium" data-id="element-175">
          Goal: Encourage booth visit as HCPs approach the venue
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[0.8fr_1.2fr] gap-5 min-h-0" data-id="element-176">
        {/* Left Column */}
        <div className="flex flex-col gap-3 overflow-auto" data-id="element-177">
          <motion.div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100" variants={container} initial="hidden" animate="show" data-id="element-178">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-179">
              <span className="w-6 h-1 bg-[#C5000F] rounded-full" data-id="element-180"></span>
              Tactics
            </h3>
            <ul className="space-y-2.5" data-id="element-181">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-182">
                <MapPin className="w-4 h-4 text-[#C5000F] mt-0.5 shrink-0" data-id="element-183" />
                <span className="text-gray-700 text-sm" data-id="element-184">
                  <strong className="text-[#2D2926]" data-id="element-185">Geo-targeted ads</strong>{' '}
                  in ride-share apps near congress venue
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-186">
                <Bus className="w-4 h-4 text-[#C5000F] mt-0.5 shrink-0" data-id="element-187" />
                <span className="text-gray-700 text-sm" data-id="element-188">
                  Bus stop/outdoor ads with QR codes
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-189">
                <Tablet className="w-4 h-4 text-[#C5000F] mt-0.5 shrink-0" data-id="element-190" />
                <span className="text-gray-700 text-sm" data-id="element-191">
                  Rep-activated iPads at hotel lobbies
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-192">
                <Gamepad2 className="w-4 h-4 text-[#C5000F] mt-0.5 shrink-0" data-id="element-193" />
                <span className="text-gray-700 text-sm" data-id="element-194">
                  Mini-quiz or peer poll to spark curiosity
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#C5000F]/5 p-4 rounded-xl border border-[#C5000F]/10" variants={container} initial="hidden" animate="show" data-id="element-195">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-196">
              <span className="w-6 h-1 bg-[#E60012] rounded-full" data-id="element-197"></span>
              Snakker™ Features
            </h3>
            <ul className="space-y-2.5" data-id="element-198">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-199">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-200" />
                <span className="text-gray-700 text-sm" data-id="element-201">
                  Teaser Snaks™ with{' '}
                  <strong className="text-[#2D2926]" data-id="element-202">
                    Multiple Select Cue™
                  </strong>
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-203">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-204" />
                <span className="text-gray-700 text-sm" data-id="element-205">
                  QR-based{' '}
                  <strong className="text-[#2D2926]" data-id="element-206">
                    scan-to-engage analytics
                  </strong>
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-207">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-208" />
                <span className="text-gray-700 text-sm" data-id="element-209">
                  Seamless CTA to drive footfall to CSL booth
                </span>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Right Column — Uber geo-targeted ad flow */}
        <motion.div className="flex flex-col items-center justify-center min-h-0" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-210">
          {/* Flow labels */}
          <motion.div className="flex items-center gap-2 mb-4 bg-white rounded-full px-4 py-1.5 shadow-sm border border-gray-100" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-211">
            <Car className="w-4 h-4 text-black" data-id="element-212" />
            <span className="text-xs font-bold text-[#2D2926]" data-id="element-213">
              Uber Ride-Share Ad Flow
            </span>
            <MapPin className="w-4 h-4 text-[#E60012]" data-id="element-214" />
          </motion.div>

          {/* Phone mockups with step labels */}
          <div className="flex items-end gap-3 justify-center" data-id="element-215">
            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-216">
              <span className="text-[10px] font-bold text-[#2D2926] bg-gray-100 rounded-full px-2.5 py-0.5" data-id="element-217">
                ① Waiting for ride
              </span>
              <UberPhoneMockup variant="waiting" data-id="element-218" />
            </motion.div>

            {/* Arrow */}
            <motion.div className="mb-24 text-[#C5000F]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} data-id="element-219">
              <span className="text-lg font-bold" data-id="element-220">→</span>
            </motion.div>

            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55
          }} data-id="element-221">
              <span className="text-[10px] font-bold text-[#E60012] bg-[#E60012]/10 rounded-full px-2.5 py-0.5" data-id="element-222">
                ② Geo-targeted ad
              </span>
              <UberPhoneMockup variant="ad" data-id="element-223" />
            </motion.div>

            {/* Arrow */}
            <motion.div className="mb-24 text-[#C5000F]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.75
          }} data-id="element-224">
              <span className="text-lg font-bold" data-id="element-225">→</span>
            </motion.div>

            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} data-id="element-226">
              <span className="text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-2.5 py-0.5" data-id="element-227">
                ③ Booth visit CTA
              </span>
              <UberPhoneMockup variant="engaged" data-id="element-228" />
            </motion.div>
          </div>

          <motion.p className="text-xs text-gray-500 italic text-center mt-3" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} data-id="element-229">
            HCPs see CSL quiz ads in Uber while riding to the congress venue
          </motion.p>
        </motion.div>
      </div>
    </div>;
}